import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@chakra-ui/react";
import { FieldWrapper } from "../../components/form/FieldWrapper";
import { functionRegistry } from "../../utils/functionRegistry";

type Props = {
  field: any;
  value?: any;
  readOnly?: any;
  invalid?: boolean;
  submitted?: boolean;
};

const CalculatedField: React.FC<Props> = ({ field, value = "", invalid }) => {
  const [text, setText] = useState<string>(typeof value === "string" ? value : "");
  const inputRef = useRef<HTMLInputElement>(null);

  const updateMode: "onChange" | "onBlur" | "manual" =
    field?.computed?.update ?? "onChange";
  const sourceName: string | undefined = field?.computed?.from;
  const handlerKey: string | undefined = field?.computed?.handler;
  const handlerArgs: Record<string, unknown> | undefined = field?.computed?.args;
  const fallback: string = field?.computed?.fallback ?? "";

  const computeFrom = useCallback(
    (raw: unknown) => {
      try {
        const fn = handlerKey ? functionRegistry[handlerKey] : undefined;
        const out = typeof fn === "function" ? fn(raw, handlerArgs) : fallback;
        setText(out ?? "");
      } catch {
        setText(fallback);
      }
    },
    [handlerKey, handlerArgs, fallback]
  );

  const findSourceInput = useCallback((): HTMLInputElement | null => {
    if (!sourceName) return null;
    const formEl: HTMLFormElement | Document = inputRef.current?.form ?? document;
    const esc =
      (window as any).CSS?.escape?.bind((window as any).CSS) ??
      ((s: string) => s.replace(/["\\]/g, "\\$&"));
    return formEl.querySelector(`[name="${esc(sourceName)}"]`) as HTMLInputElement | null;
  }, [sourceName]);

  useEffect(() => {
    const sourceEl = findSourceInput();
    const applyFromEl = () => computeFrom(sourceEl?.value);

    applyFromEl();

    if (updateMode === "manual") return;

    const evt = updateMode === "onBlur" ? "blur" : "input";
    const onInputOrBlur = () => applyFromEl();

    const onCalcAccepted = (ev: Event) => {
      const ce = ev as CustomEvent<{ raw?: unknown }>;
      const raw = ce?.detail?.raw ?? sourceEl?.value ?? "";
      computeFrom(raw);
    };

    sourceEl?.addEventListener(evt, onInputOrBlur);
    sourceEl?.addEventListener("calc:accepted", onCalcAccepted);

    return () => {
      sourceEl?.removeEventListener(evt, onInputOrBlur);
      sourceEl?.removeEventListener("calc:accepted", onCalcAccepted);
    };
  }, [findSourceInput, computeFrom, updateMode]);

  return (
    <FieldWrapper field={field} invalid={!!invalid}>
      <Input
        ref={inputRef}
        type="text"
        value={text}
        isReadOnly
        variant="filled"
        className="calculated"
      />

      <input type="hidden" name={field.name} value={text} />
    </FieldWrapper>
  );
};

export default CalculatedField;
